/**
 * Touch Panel assignment / deployment status helpers (Phase 4 + 5.1).
 * Labels are truthful about delivery stage — no optimistic success.
 */

export const ASSIGNMENT_STATUS = Object.freeze({
  NOT_ASSIGNED: 'notAssigned',
  ASSIGNED: 'assigned',
  CHANGES_NOT_PREPARED: 'changesNotPrepared',
  PREPARED: 'prepared',
  DEPLOYING: 'deploying',
  SENT: 'sent',
  AWAITING_DEVICE: 'awaitingDevice',
  DEPLOYED: 'deployed',
  DEPLOYMENT_FAILED: 'deploymentFailed',
  TIMED_OUT: 'timedOut',
  DEVICE_OFFLINE: 'deviceOffline',
  INCOMPATIBLE: 'incompatible',
  MIXED: 'mixed',
});

export const ASSIGNMENT_STATUS_LABELS = Object.freeze({
  [ASSIGNMENT_STATUS.NOT_ASSIGNED]: 'Not Assigned',
  [ASSIGNMENT_STATUS.ASSIGNED]: 'Assigned',
  [ASSIGNMENT_STATUS.CHANGES_NOT_PREPARED]: 'Changes Not Prepared',
  [ASSIGNMENT_STATUS.PREPARED]: 'Prepared',
  [ASSIGNMENT_STATUS.DEPLOYING]: 'Deploying',
  [ASSIGNMENT_STATUS.SENT]: 'Sent',
  [ASSIGNMENT_STATUS.AWAITING_DEVICE]: 'Awaiting Device',
  [ASSIGNMENT_STATUS.DEPLOYED]: 'Deployed',
  [ASSIGNMENT_STATUS.DEPLOYMENT_FAILED]: 'Deployment Failed',
  [ASSIGNMENT_STATUS.TIMED_OUT]: 'Timed Out',
  [ASSIGNMENT_STATUS.DEVICE_OFFLINE]: 'Device Offline',
  [ASSIGNMENT_STATUS.INCOMPATIBLE]: 'Device Incompatible',
  [ASSIGNMENT_STATUS.MIXED]: 'Mixed Status',
});

/**
 * Per-device delivery status labels (MQTT / Agent lifecycle).
 * Sent = MQTT publish succeeded (not yet applied).
 * Awaiting Device = no final Agent result yet.
 * Applied = Agent-confirmed activation only.
 */
export const DELIVERY_STATUS_LABELS = Object.freeze({
  queued: 'Queued',
  publishing: 'Publishing',
  sent: 'Sent',
  acknowledged: 'Awaiting Device',
  validating: 'Validating',
  applying: 'Applying',
  succeeded: 'Applied',
  failed: 'Failed',
  timedOut: 'Timed Out',
  cancelled: 'Cancelled',
  deploying: 'Deploying',
});

export function deliveryStatusLabel(status) {
  if (!status) return '—';
  return DELIVERY_STATUS_LABELS[status] || status;
}

export function deliveryStatusColor(status) {
  if (status === 'succeeded') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'timedOut') return 'warning';
  if (status === 'queued' || status === 'sent' || status === 'acknowledged') return 'info';
  return 'default';
}

function isAgentOnline(deviceOnline) {
  if (deviceOnline === false || deviceOnline === 'offline') return false;
  if (deviceOnline === true || deviceOnline === 'online') return true;
  return null;
}

/**
 * Status for a single assignment relative to the current panel revision.
 * Deployed only after Agent-confirmed activation (deployedPanelRevision).
 * Timed Out is distinct from Failed.
 * Offline is based on Agent status, not merely a delivery timeout.
 * No optimistic success from MQTT "sent".
 */
export function computeAssignmentStatus({
  assignment = null,
  panelRevision = null,
  deviceCompatibility = null,
  deviceOnline = null,
} = {}) {
  if (deviceCompatibility === 'incompatible') {
    return ASSIGNMENT_STATUS.INCOMPATIBLE;
  }
  if (!assignment || assignment.enabled === false) {
    return ASSIGNMENT_STATUS.NOT_ASSIGNED;
  }

  const currentRev = Number(panelRevision);
  const preparedRev =
    assignment.preparedPanelRevision == null
      ? null
      : Number(assignment.preparedPanelRevision);
  const deployedRev =
    assignment.deployedPanelRevision == null
      ? null
      : Number(assignment.deployedPanelRevision);

  const lastStatus = assignment.lastDeploymentStatus || null;
  const online = isAgentOnline(deviceOnline);

  // In-flight delivery — never treat as Deployed
  if (lastStatus === 'deploying') {
    return ASSIGNMENT_STATUS.DEPLOYING;
  }
  if (lastStatus === 'sent') {
    return ASSIGNMENT_STATUS.SENT;
  }
  if (lastStatus === 'acknowledged') {
    return ASSIGNMENT_STATUS.AWAITING_DEVICE;
  }

  // Terminal attempt outcomes (distinct)
  if (lastStatus === 'timedOut') {
    return ASSIGNMENT_STATUS.TIMED_OUT;
  }
  if (lastStatus === 'failed') {
    return ASSIGNMENT_STATUS.DEPLOYMENT_FAILED;
  }

  // Agent-confirmed activation only
  if (!Number.isNaN(deployedRev) && deployedRev === currentRev) {
    return ASSIGNMENT_STATUS.DEPLOYED;
  }

  // Offline from Agent heartbeat/status — not from timedOut
  if (online === false) {
    if (preparedRev != null && preparedRev === currentRev) {
      return ASSIGNMENT_STATUS.DEVICE_OFFLINE;
    }
  }

  if (preparedRev == null || Number.isNaN(preparedRev)) {
    return ASSIGNMENT_STATUS.CHANGES_NOT_PREPARED;
  }
  if (!Number.isNaN(currentRev) && preparedRev === currentRev) {
    return ASSIGNMENT_STATUS.PREPARED;
  }
  return ASSIGNMENT_STATUS.CHANGES_NOT_PREPARED;
}

/**
 * Aggregate status across assignments for a panel list row.
 */
export function computePanelDeploymentState({
  assignments = [],
  panelRevision = null,
  deviceCompatibilityById = {},
  deviceOnlineById = {},
} = {}) {
  const active = (assignments || []).filter((a) => a && a.enabled !== false);
  if (active.length === 0) {
    return ASSIGNMENT_STATUS.NOT_ASSIGNED;
  }

  const statuses = active.map((a) =>
    computeAssignmentStatus({
      assignment: a,
      panelRevision,
      deviceCompatibility: deviceCompatibilityById[a.deviceId] || null,
      deviceOnline: deviceOnlineById[a.deviceId],
    })
  );

  const unique = new Set(statuses);
  if (unique.size > 1) {
    return ASSIGNMENT_STATUS.MIXED;
  }
  return statuses[0];
}

/**
 * Concise multi-device summary: "2 deployed · 1 stale · 1 timed out"
 */
export function summarizePanelAssignmentStatuses({
  assignments = [],
  panelRevision = null,
  deviceCompatibilityById = {},
  deviceOnlineById = {},
} = {}) {
  const active = (assignments || []).filter((a) => a && a.enabled !== false);
  if (active.length === 0) return 'Not assigned';

  const counts = {};
  active.forEach((a) => {
    const s = computeAssignmentStatus({
      assignment: a,
      panelRevision,
      deviceCompatibility: deviceCompatibilityById[a.deviceId] || null,
      deviceOnline: deviceOnlineById[a.deviceId],
    });
    counts[s] = (counts[s] || 0) + 1;
  });

  const parts = [];
  const order = [
    ASSIGNMENT_STATUS.DEPLOYED,
    ASSIGNMENT_STATUS.DEPLOYING,
    ASSIGNMENT_STATUS.SENT,
    ASSIGNMENT_STATUS.AWAITING_DEVICE,
    ASSIGNMENT_STATUS.PREPARED,
    ASSIGNMENT_STATUS.CHANGES_NOT_PREPARED,
    ASSIGNMENT_STATUS.DEPLOYMENT_FAILED,
    ASSIGNMENT_STATUS.TIMED_OUT,
    ASSIGNMENT_STATUS.DEVICE_OFFLINE,
    ASSIGNMENT_STATUS.INCOMPATIBLE,
  ];
  order.forEach((key) => {
    if (counts[key]) {
      let label;
      if (key === ASSIGNMENT_STATUS.CHANGES_NOT_PREPARED) {
        label = 'stale';
      } else if (key === ASSIGNMENT_STATUS.TIMED_OUT) {
        label = 'timed out';
      } else {
        label = (ASSIGNMENT_STATUS_LABELS[key] || key).toLowerCase();
      }
      parts.push(`${counts[key]} ${label}`);
    }
  });
  return parts.join(' · ') || 'Assigned';
}

export function isAssignmentStale(assignment, panelRevision) {
  if (!assignment || assignment.enabled === false) return false;
  const deployed = assignment.deployedPanelRevision;
  if (deployed != null && Number(deployed) === Number(panelRevision)) return false;
  if (assignment.preparedPanelRevision == null) return true;
  return Number(assignment.preparedPanelRevision) !== Number(panelRevision);
}
